<!--
  MarkdownEditModal.vue

  The modal editing surface for a markdown field's expand action — shared by
  MarkdownInput.vue's expand button (record-form context) and MarkdownCell's
  expand icon (in the grid), so both open the exact same UI. Owns the
  Teleport + ModalContainer + editable MarkdownEditor + draft/save/cancel
  state; a caller holds a template ref and calls `open()`.
-->
<template>
  <Teleport to="body">
    <ModalContainer :show="showModal" @close="cancel">
      <div class="markdown-modal">
        <MarkdownEditor v-model="draft" :editable="true" />
        <div class="modal-actions flex g-2 mt-3 justify-end">
          <button class="btn" @click="cancel">Cancel</button>
          <button class="btn btn-primary" @click="save">Save</button>
        </div>
      </div>
    </ModalContainer>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from "vue"
import ModalContainer from "@auger/ui/components/ModalContainer.vue"
import MarkdownEditor from "@/components/editors/MarkdownEditor.vue"

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
  }>(),
  {
    modelValue: "",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: string]
  cancel: []
}>()

const showModal = ref(false)
const draft = ref("")
// The value the modal opened with, to tell a real edit from an unchanged one.
const original = ref("")

function open() {
  original.value = props.modelValue ?? ""
  draft.value = original.value
  showModal.value = true
}

function save() {
  // Unchanged — close without writing (and without a no-op history entry).
  if (draft.value !== original.value) emit("update:modelValue", draft.value)
  showModal.value = false
}

// Cancel, Escape and backdrop clicks all land here; confirm before dropping edits.
function cancel() {
  if (draft.value !== original.value && !window.confirm("Discard your unsaved changes?")) return
  showModal.value = false
  emit("cancel")
}

defineExpose({ open })
</script>

<style>
.markdown-modal {
  width: min(80vw, 800px);

  .markdown-editor .ProseMirror[contenteditable="true"] {
    min-height: 50vh;
  }
}
</style>
